// React Query hooks for SITROOM REST API.
// Reads/writes go through this single layer — components stay free of fetch logic.
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---- shared keys ----------------------------------------------------------
const KEYS = {
  incidents: ["incidents"],
  incident: (id) => ["incidents", id],
  eightds: ["eightds"],
  eightd: (id) => ["eightds", id],
  fmeas: ["fmeas"],
  lpas: ["lpas"],
  tenants: ["tenants"],
};

// ---- low-level fetchers ---------------------------------------------------
const get = async (path) => (await axios.get(`${API}${path}`)).data;
const post = async (path, body) => (await axios.post(`${API}${path}`, body)).data;
const patch = async (path, body) => (await axios.patch(`${API}${path}`, body)).data;
const del = async (path) => (await axios.delete(`${API}${path}`)).data;

// ============================ INCIDENTS ====================================
export const useIncidents = (queryString = "") =>
  useQuery({
    queryKey: queryString ? [...KEYS.incidents, queryString] : KEYS.incidents,
    queryFn: () => get(`/incidents${queryString ? "?" + queryString : ""}`),
  });

export const useIncident = (id) =>
  useQuery({
    queryKey: KEYS.incident(id),
    queryFn: () => get(`/incidents/${id}`),
    enabled: Boolean(id),
  });

export const useCreateIncident = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post("/incidents", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.incidents }),
  });
};

export const usePatchIncident = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch: body }) => patch(`/incidents/${id}`, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.incidents });
      qc.invalidateQueries({ queryKey: KEYS.incident(vars.id) });
    },
  });
};

export const useAddComment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }) => post(`/incidents/${id}/comments`, comment),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.incidents });
      qc.invalidateQueries({ queryKey: KEYS.incident(vars.id) });
    },
  });
};

// ============================ 8D REPORTS ===================================
export const useEightDs = () =>
  useQuery({ queryKey: KEYS.eightds, queryFn: () => get("/eightds") });

export const useEightD = (id) =>
  useQuery({
    queryKey: KEYS.eightd(id),
    queryFn: () => get(`/eightds/${id}`),
    enabled: Boolean(id),
  });

export const useCreateEightD = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post("/eightds", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.eightds }),
  });
};

export const usePatchEightD = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch: body }) => patch(`/eightds/${id}`, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.eightds });
      qc.invalidateQueries({ queryKey: KEYS.eightd(vars.id) });
    },
  });
};

// ============================ FMEAs ========================================
export const useFmeas = () =>
  useQuery({ queryKey: KEYS.fmeas, queryFn: () => get("/fmeas") });

export const useCreateFmea = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post("/fmeas", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.fmeas }),
  });
};

export const usePatchFmea = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch: body }) => patch(`/fmeas/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.fmeas }),
  });
};

// ============================ LPAs =========================================
export const useLpas = () =>
  useQuery({ queryKey: KEYS.lpas, queryFn: () => get("/lpas") });

export const useCreateLpa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post("/lpas", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.lpas }),
  });
};

// ============================ TENANTS ======================================
export const useTenants = () =>
  useQuery({ queryKey: KEYS.tenants, queryFn: () => get("/tenants") });

// ============================ ADMIN USERS ==================================
export const useAdminUsers = (enabled = true) =>
  useQuery({
    queryKey: ["admin-users"],
    queryFn: () => get("/admin/users"),
    enabled,
  });

export const useCreateAdminUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => post("/admin/users", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
};

export const usePatchAdminUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch: body }) => patch(`/admin/users/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
};

export const useDeleteAdminUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => del(`/admin/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
};

// Iter79 — admin reset password (returns temp password when not provided).
export const useAdminResetPassword = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => post(`/admin/users/${id}/reset-password`, body || {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
};

// ============================ ADMIN INVITE / APPROVAL FLOW (iter46-B) ======
// Tenant admin invite → creates pending_approval user
export const useInviteUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => post("/admin/users/invite", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-pending-users"] });
    },
  });
};

// Platform admin sees ALL pending; tenant admin sees own-tenant pending
export const usePendingUsers = (enabled = true) =>
  useQuery({
    queryKey: ["admin-pending-users"],
    queryFn: () => get("/admin/users/pending"),
    enabled,
    refetchOnWindowFocus: true,
  });

export const useApproveUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) => post(`/admin/users/${id}/approve`, { reason: reason || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-pending-users"] });
    },
  });
};

export const useRejectUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) => post(`/admin/users/${id}/reject`, { reason: reason || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-pending-users"] });
    },
  });
};

// ============================ ADMIN INCIDENT TEMPLATES (iter46-B) ==========
export const useIncidentTemplates = (enabled = true) =>
  useQuery({
    queryKey: ["incident-templates"],
    queryFn: () => get("/admin/incident-templates"),
    enabled,
  });

export const useCreateIncidentTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => post("/admin/incident-templates", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["incident-templates"] }),
  });
};

export const useUpdateIncidentTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ template_key, body }) => patch(`/admin/incident-templates/${template_key}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["incident-templates"] }),
  });
};

export const useDeleteIncidentTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (template_key) => del(`/admin/incident-templates/${template_key}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["incident-templates"] }),
  });
};

// ============================ ADMIN SR TYPE CONFIG (iter46-B) ==============
export const useSrTypeConfig = (enabled = true) =>
  useQuery({
    queryKey: ["sr-type-config"],
    queryFn: () => get("/admin/sr-type-config"),
    enabled,
  });

export const useUpsertSrTypeConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => post("/admin/sr-type-config", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sr-type-config"] }),
  });
};

// ============================ ADMIN MASTER DATA (iter47-A) =================
// SR Types master
export const useSrTypeMaster = (enabled = true) =>
  useQuery({ queryKey: ["sr-types-master"], queryFn: () => get("/admin/sr-types"), enabled });

export const useCreateSrType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => post("/admin/sr-types", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sr-types-master"] }),
  });
};

export const useUpdateSrType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, body }) => patch(`/admin/sr-types/${key}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sr-types-master"] }),
  });
};

export const useDeleteSrType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key) => del(`/admin/sr-types/${key}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sr-types-master"] }),
  });
};

// Tenant Types
export const useTenantTypes = (enabled = true) =>
  useQuery({ queryKey: ["tenant-types"], queryFn: () => get("/admin/tenant-types"), enabled });

export const useCreateTenantType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => post("/admin/tenant-types", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant-types"] }),
  });
};

export const useUpdateTenantType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, body }) => patch(`/admin/tenant-types/${key}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant-types"] }),
  });
};

export const useDeleteTenantType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key) => del(`/admin/tenant-types/${key}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant-types"] }),
  });
};

// Incident Categories
export const useIncidentCategories = (enabled = true) =>
  useQuery({ queryKey: ["incident-categories"], queryFn: () => get("/admin/incident-categories"), enabled });

export const useCreateIncidentCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => post("/admin/incident-categories", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["incident-categories"] }),
  });
};

export const useUpdateIncidentCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, body }) => patch(`/admin/incident-categories/${key}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["incident-categories"] }),
  });
};

export const useDeleteIncidentCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key) => del(`/admin/incident-categories/${key}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["incident-categories"] }),
  });
};

// ============================ ADMIN TENANTS + RELATIONSHIPS (iter47-B) =====
export const useCreateTenant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => post("/admin/tenants", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.tenants }),
  });
};

export const useUpdateTenant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => patch(`/admin/tenants/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.tenants }),
  });
};

export const useDeleteTenant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => del(`/admin/tenants/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.tenants }),
  });
};

// Contacts
export const useAddTenantContact = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenant_id, body }) => post(`/admin/tenants/${tenant_id}/contacts`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.tenants }),
  });
};

export const useUpdateTenantContact = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenant_id, contact_id, body }) => patch(`/admin/tenants/${tenant_id}/contacts/${contact_id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.tenants }),
  });
};

export const useDeleteTenantContact = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenant_id, contact_id }) => del(`/admin/tenants/${tenant_id}/contacts/${contact_id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.tenants }),
  });
};

// Relationships
export const useRelationships = (filters = {}) =>
  useQuery({
    queryKey: ["relationships", filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      if (filters.part) params.set("part", filters.part);
      const qs = params.toString();
      return get(`/admin/relationships${qs ? `?${qs}` : ""}`);
    },
  });

export const useCreateRelationship = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => post("/admin/relationships", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["relationships"] }),
  });
};

export const useUpdateRelationship = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => patch(`/admin/relationships/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["relationships"] }),
  });
};

export const useDeleteRelationship = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => del(`/admin/relationships/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["relationships"] }),
  });
};

// Admin parts CRUD
export const usePart = (partNumber, enabled = true) =>
  useQuery({
    queryKey: ["part", partNumber],
    queryFn: () => get(`/parts?q=${encodeURIComponent(partNumber)}`),
    enabled: enabled && !!partNumber,
  });

export const useUpsertPart = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => post("/admin/parts", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["parts"] }),
  });
};

export const useUpdatePart = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pn, body }) => patch(`/admin/parts/${encodeURIComponent(pn)}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["parts"] }),
  });
};

export const useDeletePart = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pn) => del(`/admin/parts/${encodeURIComponent(pn)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["parts"] }),
  });
};

// Part routing lookup — feeds the wizard
export const usePartRouting = (partNumber, enabled = true) =>
  useQuery({
    queryKey: ["part-routing", partNumber],
    queryFn: () => get(`/parts/${encodeURIComponent(partNumber)}/routing`),
    enabled: enabled && !!partNumber && partNumber.length >= 3,
    staleTime: 30_000,
  });

// ============================ PARTS & PLANTS ===============================
export const useParts = (q = "") =>
  useQuery({
    queryKey: ["parts", q],
    queryFn: () => get(`/parts${q ? `?q=${encodeURIComponent(q)}` : ""}`),
    keepPreviousData: true,
  });

export const usePlants = () =>
  useQuery({ queryKey: ["plants"], queryFn: () => get("/plants") });

export const uploadAttachments = async (incidentId, files) => {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  const res = await axios.post(`${API}/incidents/${incidentId}/attachments`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return res.data;
};

// ============================ AUDIT LOGS ===================================
export const useAuditLogs = (params = {}) =>
  useQuery({
    queryKey: ["audit-logs", params],
    queryFn: () => {
      const qs = Object.entries(params)
        .filter(([, v]) => v !== "" && v !== null && v !== undefined)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");
      return get(`/audit-logs${qs ? `?${qs}` : ""}`);
    },
    keepPreviousData: true,
  });

export const useAuditSummary = () =>
  useQuery({ queryKey: ["audit-summary"], queryFn: () => get("/audit-logs/summary") });

// ============================ GLOBAL SEARCH ================================
export const useGlobalSearch = (q, enabled = true) =>
  useQuery({
    queryKey: ["search", q],
    queryFn: () => get(`/search?q=${encodeURIComponent(q)}`),
    enabled: enabled && (q || "").trim().length >= 2,
    keepPreviousData: true,
  });

// ============================ PARTICIPANTS (for @-mention) =================
export const useIncidentParticipants = (incidentId, visibility = "all_parties", enabled = true) =>
  useQuery({
    queryKey: ["participants", incidentId, visibility],
    queryFn: () => get(`/incidents/${incidentId}/participants?visibility=${visibility}`),
    enabled: enabled && Boolean(incidentId),
    keepPreviousData: true,
  });

// ============================ MENTION NOTIFICATIONS ========================
export const useUnreadMentions = () =>
  useQuery({
    queryKey: ["mentions-unread"],
    queryFn: () => get("/notifications/mentions/unread"),
    refetchOnWindowFocus: true,
  });

export const useMarkIncidentMentionsRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (incidentId) => post(`/notifications/mentions/${incidentId}/read`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mentions-unread"] });
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-count"] });
    },
  });
};

// ============================ INBOX (iter16+iter30) =======================
export const useInbox = () =>
  useQuery({
    queryKey: ["inbox"],
    queryFn: () => get("/notifications/inbox"),
    refetchOnWindowFocus: true,
  });

// Paginated + filterable inbox for the /notifications full-page log.
export const useInboxPage = ({ filter, page = 1, limit = 50 } = {}) =>
  useQuery({
    queryKey: ["inbox-page", filter || "all", page, limit],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filter && filter !== "all") qs.set("filter", filter);
      qs.set("page", String(page));
      qs.set("limit", String(limit));
      return get(`/notifications/inbox?${qs.toString()}`);
    },
    keepPreviousData: true,
    refetchOnWindowFocus: true,
  });

export const useInboxCount = () =>
  useQuery({
    queryKey: ["inbox-count"],
    queryFn: () => get("/notifications/inbox/count"),
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

export const useMarkInboxItemRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => post(`/notifications/inbox/${id}/read`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-count"] });
    },
  });
};

export const useMarkAllInboxRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post(`/notifications/inbox/read-all`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-page"] });
      qc.invalidateQueries({ queryKey: ["inbox-count"] });
    },
  });
};

export const useDeleteInboxItem = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => del(`/notifications/inbox/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-page"] });
      qc.invalidateQueries({ queryKey: ["inbox-count"] });
    },
  });
};

export const useDeleteAllReadInbox = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => del(`/notifications/inbox/read-all`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-page"] });
      qc.invalidateQueries({ queryKey: ["inbox-count"] });
    },
  });
};

// ============================ INTEGRATIONS (iter17) ========================
export const useIntegrations = (enabled = true) =>
  useQuery({
    queryKey: ["integrations"],
    queryFn: () => get("/integrations"),
    enabled,
    refetchOnWindowFocus: true,
  });

export const useUpdateIntegrations = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patchBody) => patch("/integrations", patchBody),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
  });
};

export const useTestWebhook = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (type) => post(`/webhooks/test?type=${encodeURIComponent(type)}`, {}),
    // Use onSettled so we refresh status even when the test POST fails (502),
    // because the backend still records last_sent + last_status='failed'.
    onSettled: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
  });
};

// ============================ SERVICE REQUESTS (iter18) ====================
export const useServiceRequests = (incidentId, enabled = true) =>
  useQuery({
    queryKey: ["service-requests", incidentId],
    queryFn: () => get(`/incidents/${incidentId}/service-requests`),
    enabled: enabled && Boolean(incidentId),
    refetchOnWindowFocus: true,
  });

export const useCreateServiceRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, body }) => post(`/incidents/${incidentId}/service-requests`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["service-requests", vars.incidentId] });
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-count"] });
    },
  });
};

export const usePatchServiceRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sr_id, body }) => patch(`/service-requests/${sr_id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service-requests"] });
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-count"] });
    },
  });
};

export const useCancelServiceRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sr_id) => del(`/service-requests/${sr_id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service-requests"] });
    },
  });
};


// Low-level (one-off) — for places that aren't in React rendering tree
export const apiPatchFmea = (id, body) => patch(`/fmeas/${id}`, body);
export const apiPatchIncident = (id, body) => patch(`/incidents/${id}`, body);
export const apiPatchEightD = (id, body) => patch(`/eightds/${id}`, body);
